<template>
    <div v-if="!hideProduct"
         :class="[
             'relative flex flex-col md:flex-row border border-gray-200 rounded-xl overflow-hidden shadow-md bg-white',
             isSoldOut ? 'opacity-70 scale-95 transform' : ''
         ]">
        <div v-if="showPopularRibbon || showLimitedSpacesRibbon || showElectricRibbon || showMostFlexibleRibbon" class="absolute right-[-40px] top-[25px] w-[200px] z-10 ml-5">
            <div :class="[ribbonBgColorClass]"
                 class="text-center font-semibold text-xs py-1 px-4 text-white uppercase shadow-md transform rotate-45 whitespace-normal leading-tight">
               <span v-if="showLimitedSpacesRibbon && this.max_per_day > 0" class="uppercase block">
               <template v-if="this.current_per_day < this.max_per_day">
                    {{ this.max_per_day - this.current_per_day }} {{ this.max_per_day - this.current_per_day === 1 ? 'SPACE' : 'SPACES' }}<br>REMAINING
                </template>
                <template v-else>
                    SOLD<br>OUT
                </template>
             </span>
                <span v-else-if="showPopularRibbon" class="uppercase block">MOST<br>POPULAR</span>
                <span v-else-if="showElectricRibbon" class="uppercase block">{{ electricAvailable }} {{ electricAvailable === 1 ? 'SPACE' : 'SPACES' }}<br>REMAINING</span>
                <span v-else-if="showMostFlexibleRibbon" class="uppercase block">MOST<br>FLEXIBLE</span>
            </div>
        </div>

        <div class="md:flex-shrink-0 w-full md:w-52 relative">
            <div class="pb-[56%] md:pb-0 md:h-full">
                <img :src="image" alt="Product Image" class="absolute inset-0 w-full h-full object-cover">
            </div>
        </div>

        <div class="flex-grow p-5 md:p-6 flex flex-col gap-4">
            <!-- Product header + discount badge -->
            <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                    <h2 :class="[productTextColorClass]" class="text-xl md:text-2xl font-extrabold uppercase leading-tight">{{ name }}</h2>
                    <p v-html="description" class="text-gray-600 text-sm mt-1 leading-snug"></p>
                    <p class="text-gray-400 text-xs mt-1.5">CCTV &amp; floodlit · full perimeter fencing · open 24/7, 365 days</p>
                    <p v-if="daysLeft >= 0" class="text-red-500 font-bold text-xs mt-1.5">Limited time offer: {{ daysLeft }} days left</p>
                </div>
                <span v-if="discountLabel" class="shrink-0 inline-flex items-center bg-green-100 text-green-700 text-xs font-extrabold px-2.5 py-1 rounded uppercase tracking-wide">
                    {{ discountLabel }}
                </span>
            </div>

            <!-- Choose your vehicle: Car or Van; picking Van reveals the sizes -->
            <div>
                <div class="text-xs font-semibold text-gray-500 mb-1.5">Choose your vehicle:</div>
                <div class="flex flex-wrap gap-2">
                    <button type="button" @click="selectedType = 'car'"
                        :class="selectedType === 'car' ? [productBorderColorClass, productSoftBgClass] : 'border-gray-200 bg-white hover:border-gray-300'"
                        class="flex-1 min-w-[130px] border-2 rounded-lg px-3 py-2 text-left cursor-pointer transition-colors">
                        <div class="flex items-center justify-between">
                            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Car</span>
                            <span v-if="selectedType === 'car'" :class="productTextColorClass" class="text-xs font-bold">✓</span>
                        </div>
                        <div class="flex items-baseline gap-1.5 mt-0.5">
                            <span v-if="hasDiscount" class="text-gray-400 line-through text-xs">£{{ preDiscountPrice }}</span>
                            <span :class="hasDiscount ? 'text-green-700' : 'text-gray-900'" class="text-lg font-extrabold">£{{ mainPrice }}</span>
                        </div>
                    </button>

                    <!-- Van category: expands into the size options below -->
                    <button v-if="vanFromPrice !== null" type="button" @click="selectVan()"
                        :class="isVanSelected ? [productBorderColorClass, productSoftBgClass] : 'border-gray-200 bg-white hover:border-gray-300'"
                        class="flex-1 min-w-[130px] border-2 rounded-lg px-3 py-2 text-left cursor-pointer transition-colors">
                        <div class="flex items-center justify-between">
                            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Van</span>
                            <span v-if="isVanSelected" :class="productTextColorClass" class="text-xs font-bold">✓</span>
                        </div>
                        <div class="flex items-baseline gap-1.5 mt-0.5">
                            <span class="text-lg font-extrabold text-gray-900">from £{{ vanFromPrice }}</span>
                        </div>
                    </button>
                </div>

                <!-- Van sizes, shown once Van is chosen -->
                <div v-if="isVanSelected" class="mt-2">
                    <div class="text-xs font-semibold text-gray-500 mb-1.5">Choose your van size:</div>
                    <div class="flex flex-wrap gap-2">
                        <button v-for="(item, key) in vanSizeOptions" :key="key" type="button" @click="selectedType = key"
                            :class="selectedType === key ? [productBorderColorClass, productSoftBgClass] : 'border-gray-200 bg-white hover:border-gray-300'"
                            class="flex-1 min-w-[130px] border-2 rounded-lg px-3 py-2 text-left cursor-pointer transition-colors">
                            <div class="flex items-center justify-between">
                                <span class="text-xs font-bold text-gray-600 leading-tight">{{ vehicleNote(item.label) || vehicleName(item.label) || key }}</span>
                                <span v-if="selectedType === key" :class="productTextColorClass" class="text-xs font-bold">✓</span>
                            </div>
                            <div class="flex items-baseline gap-1.5 mt-0.5">
                                <span v-if="item.has_discount" class="text-gray-400 line-through text-xs">£{{ item.pre_discount }}</span>
                                <span :class="item.has_discount ? 'text-green-700' : 'text-gray-900'" class="text-lg font-extrabold">£{{ item.adjusted }}</span>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Transfer note + CTA -->
            <div class="flex items-center justify-between gap-4 pt-3 border-t border-gray-100">
                <span class="text-gray-400 text-xs font-semibold">Transfer time of 5 mins by free shuttle bus to airport terminal.</span>
                <a v-if="this.max_per_day === null || this.current_per_day === null || this.current_per_day < this.max_per_day"
                   :href="`/quick-booking/${id}?vehicle_type=${selectedType}`"
                   :class="[productBgColorClass]"
                   class="shrink-0 text-white font-bold py-3 px-7 rounded-lg shadow-lg text-base uppercase transition-colors duration-200 ease-in-out hover:opacity-90">
                    Select &amp; Pay
                </a>
                <span v-else
                      class="shrink-0 bg-gray-400 text-white font-bold py-3 px-7 rounded-lg text-base uppercase cursor-not-allowed">
                    Sold Out
                </span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            // Which vehicle pill is chosen; carried to checkout via the CTA.
            selectedType: 'car',
        };
    },
    props: {
        id: {
            type: Number,
            required: true,
        },
        name: {
            type: String,
            required: true,
        },
        description: {
            type: String,
            required: true,
        },
        image: {
            type: String,
            required: true,
        },
        price: {
            type: Number,
            required: true,
        },
        start_ts: {
            type: String,
            required: false,
        },
        end_ts: {
            type: String,
            required: false,
        },
        prices: {
            type: Object,
            required: false,
        },
        operation: {
            type: String,
            required: false,
        },
        active: {
            type: Boolean,
            required: false,
        },
        current_per_day: {
            type: Number,
            required: false,
        },
        max_per_day: {
            type: Number,
            required: false,
        },
        electricAvailable: {
            type: Number,
            required: false,
            default: 6,
        },
    },
    computed: {
        ribbonBgColorClass() {
            const productName = this.name.toLowerCase();
            if (productName.includes('super saver')) {
                return 'bg-red-600';
            } if (productName.includes('lowcost saver')) {
                return 'bg-orange-600';
            } if (productName.includes('flying scot')) {
                return 'bg-blue-600';
            } if (productName.includes('electric')) {
                return 'bg-green-600';
            }
            return 'bg-orange-600';
        },

        showLimitedSpacesRibbon() {
            return this.max_per_day > 0;
        },
        daysLeft() {
            if (this.start_ts && this.end_ts) {
                const endDate = new Date(this.end_ts);
                const currentDate = new Date();
                const timeDiff = endDate - currentDate;
                const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
                return daysLeft >= 0 ? daysLeft : -1;
            }
            return -1;
        },
        showPopularRibbon() {
            return this.name.toLowerCase().includes('lowcost saver');
        },
        filteredOtherPrices() {
            const filteredPrices = {};
            // Van first, then larger vehicles; skip the discount metadata key.
            const order = ['van', '4x4'];
            for (const key of order) {
                if (this.otherPrices.hasOwnProperty(key)) {
                    filteredPrices[key] = this.otherPrices[key];
                }
            }
            return filteredPrices;
        },
        // The van sizes (van + larger vehicle) offered under the Van category.
        vanSizeOptions() {
            return this.filteredOtherPrices;
        },
        isVanSelected() {
            return Object.keys(this.vanSizeOptions).includes(this.selectedType);
        },
        // Cheapest van-size price, shown as "from £X" on the Van category pill.
        vanFromPrice() {
            const prices = Object.values(this.vanSizeOptions)
                .map((o) => Number(o.adjusted))
                .filter((n) => !Number.isNaN(n));
            return prices.length ? Math.min(...prices) : null;
        },
        showElectricRibbon() {
            return this.name.toLowerCase().includes('electric');
        },
        showMostFlexibleRibbon() {
            return this.name.toLowerCase().includes('flying scot');
        },
        mainPrice() {
            return this.prices && this.prices.car && typeof this.prices.car.adjusted === 'number'
                ? this.prices.car.adjusted
                : this.price;
        },
        hasDiscount() {
            return Boolean(this.prices && this.prices.car && this.prices.car.has_discount);
        },
        preDiscountPrice() {
            return this.prices && this.prices.car ? this.prices.car.pre_discount : null;
        },
        discountLabel() {
            const discount = this.prices && this.prices.adhoc_discount;

            if (discount) {
                return discount.type === 'percentage' ? `${discount.value}% off` : `£${discount.value} off`;
            }

            // No discount is running, so the struck-through figure is the
            // drive-up price. Say so - an unexplained slashed price reads as a
            // sale that isn't happening.
            if (this.isWalkInComparison && this.walkInSaving > 0) {
                return `Save £${this.walkInSaving.toFixed(2)} booking online`;
            }

            return '';
        },
        isWalkInComparison() {
            return Boolean(this.prices && this.prices.car && this.prices.car.comparison === 'walk_in');
        },
        walkInSaving() {
            if (!this.isWalkInComparison) return 0;

            return this.prices.car.pre_discount - this.prices.car.adjusted;
        },
        otherPrices() {
            const filteredPrices = {};
            for (const key in this.prices) {
                if (key !== 'car' && this.prices.hasOwnProperty(key)) {
                    filteredPrices[key] = this.prices[key];
                }
            }
            return filteredPrices;
        },
        hideProduct() {
            return this.active === false || (this.max_per_day !== null && this.current_per_day >= this.max_per_day) || (this.start_ts && this.end_ts && this.daysLeft < 0);
        },
        showBanner() {
            return this.max_per_day > 0;
        },

        remainingSpaces() {
            return this.max_per_day - this.current_per_day;
        },
        isSoldOut() {
            return this.max_per_day !== null && this.current_per_day !== null && this.current_per_day >= this.max_per_day;
        },
        productTextColorClass() {
            const productName = this.name.toLowerCase();
            if (productName.includes('super saver')) {
                return 'text-red-600'; // color for Super Saver
            } if (productName.includes('low cost')) {
                return 'text-orange-600'; // color for Low Cost Saver
            } if (productName.includes('flying scot')) {
                return 'text-blue-600'; // for Flying Scot Express
            } if (productName.includes('electric')) {
                return 'text-green-600'; // color for Electric Bay
            }
            return 'text-orange-600'; // Default color if no match
        },
        productBgColorClass() {
            const productName = this.name.toLowerCase();
            if (productName.includes('super saver')) {
                return 'bg-red-600'; // Super Saver is now red
            } if (productName.includes('low cost')) {
                return 'bg-orange-600'; // Low Cost Saver remains orange
            } if (productName.includes('flying scot')) {
                return 'bg-blue-600'; // Flying Scot Express is now blue
            } if (productName.includes('electric')) {
                return 'bg-green-600'; // Electric Bay remains green
            }
            return 'bg-orange-600'; // Default color if no match
        },
        productBorderColorClass() {
            const productName = this.name.toLowerCase();
            if (productName.includes('super saver')) return 'border-red-500';
            if (productName.includes('low cost')) return 'border-orange-500';
            if (productName.includes('flying scot')) return 'border-blue-500';
            if (productName.includes('electric')) return 'border-green-500';
            return 'border-orange-500';
        },
        productSoftBgClass() {
            const productName = this.name.toLowerCase();
            if (productName.includes('super saver')) return 'bg-red-50';
            if (productName.includes('low cost')) return 'bg-orange-50';
            if (productName.includes('flying scot')) return 'bg-blue-50';
            if (productName.includes('electric')) return 'bg-green-50';
            return 'bg-orange-50';
        },
    },
    methods: {
        // Picking the Van category defaults to the first (smallest) size and
        // reveals the size options.
        selectVan() {
            if (this.isVanSelected) return;
            const keys = Object.keys(this.vanSizeOptions);
            if (keys.length) this.selectedType = keys[0];
        },
        // Split "Van (under 17 ft / 5.2 m)" into a bold name and a small note.
        vehicleName(label) {
            return String(label || '').split('(')[0].trim();
        },
        vehicleNote(label) {
            const match = String(label || '').match(/\(([^)]*)\)/);
            return match ? match[1] : '';
        },
    },
};
</script>
